import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { clienteService, prestamoService } from '../../services/api';
import '../../styles/Clientes.css';
import { usePermisosGuard } from '../../hooks/usePermisosGuard';

const EliminarPrestamo = () => {
  usePermisosGuard('prestamos', 'eliminar');
  const navigate = useNavigate();
  const { codigo } = useParams();
  const getRoute = (path) => codigo ? `/${codigo}${path}` : path;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [prestamos, setPrestamos] = useState([]);
  const [prestamoSeleccionado, setPrestamoSeleccionado] = useState(null);
  const [searchId, setSearchId] = useState('');
  const [confirmacion, setConfirmacion] = useState(false);
  const [clientes, setClientes] = useState([]);
  const [clienteSeleccionado, setClienteSeleccionado] = useState('');
  const [prestamosPorCliente, setPrestamosPorCliente] = useState([]);
  const [busquedaCliente, setBusquedaCliente] = useState('');
  const [mostrarDropdown, setMostrarDropdown] = useState(false);
  const [clienteObj, setClienteObj] = useState(null);

  useEffect(() => {
    loadPrestamos();
    loadClientes();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('.form-group')) {
        setMostrarDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadPrestamos = async () => {
    try {
      const response = await prestamoService.getPrestamos({ per_page: 1000 });
      setPrestamos(response.data.prestamos || []);
    } catch (err) {
      console.error('Error al cargar préstamos:', err);
    }
  };

  const loadClientes = async () => {
    try {
      const response = await clienteService.getClientes({ per_page: 1000 });
      setClientes(response.data.clientes || []);
    } catch (err) {
      console.error('Error al cargar clientes:', err);
    }
  };

  const buscarPorId = async () => {
    if (!searchId) {
      setError('Ingrese un ID de préstamo');
      return;
    }

    try {
      setLoading(true);
      setError('');
      const response = await prestamoService.getPrestamo(searchId);
      setPrestamoSeleccionado(response.data);
      setConfirmacion(false);
    } catch (err) {
      console.error('Error:', err);
      setError('Préstamo no encontrado');
      setPrestamoSeleccionado(null);
    } finally {
      setLoading(false);
    }
  };

  const clientesFiltrados = clientes.filter(cliente => {
    const termino = busquedaCliente.toLowerCase().trim();
    if (!termino) return true;

    return (
      (cliente.nombres && cliente.nombres.toLowerCase().includes(termino)) ||
      (cliente.apellidos && cliente.apellidos.toLowerCase().includes(termino)) ||
      (cliente.cedula && cliente.cedula.includes(termino)) ||
      (cliente.apodo && cliente.apodo.toLowerCase().includes(termino)) ||
      cliente.id.toString().includes(termino) ||
      (cliente.nombres && cliente.apellidos &&
       `${cliente.nombres} ${cliente.apellidos}`.toLowerCase().includes(termino))
    );
  }).slice(0, 10);

  const seleccionarCliente = async (cliente) => {
    setClienteObj(cliente);
    setBusquedaCliente(`${cliente.nombres} ${cliente.apellidos} - ${cliente.cedula}`);
    setMostrarDropdown(false);
    setClienteSeleccionado(cliente.id);
    setPrestamoSeleccionado(null);
    setPrestamosPorCliente([]);
    setConfirmacion(false);
    setError('');

    try {
      setLoading(true);
      const response = await prestamoService.getPrestamos({
        cliente_id: cliente.id,
        per_page: 1000
      });
      setPrestamosPorCliente(response.data.prestamos || []);
    } catch (err) {
      console.error('Error:', err);
      setError('Error al cargar préstamos del cliente');
    } finally {
      setLoading(false);
    }
  };

  const limpiarCliente = () => {
    setClienteObj(null);
    setClienteSeleccionado('');
    setBusquedaCliente('');
    setPrestamosPorCliente([]);
    setPrestamoSeleccionado(null);
    setConfirmacion(false);
  };

  const handleSeleccionarPrestamo = async (prestamoId) => {
    try {
      setLoading(true);
      setError('');
      const response = await prestamoService.getPrestamo(prestamoId);
      setPrestamoSeleccionado(response.data);
      setConfirmacion(false);
    } catch (err) {
      console.error('Error:', err);
      setError('Error al cargar el préstamo');
    } finally {
      setLoading(false);
    }
  };

  const handleEliminar = async () => {
    if (!prestamoSeleccionado || !confirmacion) {
      setError('Debe confirmar la eliminación');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setSuccess('');

      // Cambiar el estado a cancelado en lugar de eliminar físicamente
      await prestamoService.updatePrestamo(prestamoSeleccionado.id, {
        estado: 'cancelado'
      });

      setSuccess('Préstamo eliminado exitosamente');
      setPrestamoSeleccionado(null);
      setSearchId('');
      setConfirmacion(false);

      setTimeout(() => {
        loadPrestamos();
      }, 1000);

    } catch (err) {
      console.error('Error:', err);
      setError(err.response?.data?.error || 'Error al eliminar el préstamo');
    } finally {
      setLoading(false);
    }
  };

  const formatMoney = (amount) => {
    return `$${(amount || 0).toLocaleString('es-DO', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('es-DO');
  };

  return (
    <div className="clientes-page">
      <div className="clientes-header">
        <h1>Eliminar Préstamo</h1>
        <button
          className="btn"
          onClick={() => navigate(getRoute('/prestamos'))}
          style={{background: '#666'}}
        >
          ← Volver
        </button>
      </div>

      {error && <div className="banner error">{error}</div>}
      {success && <div className="banner success">{success}</div>}

      {/* Búsqueda */}
      <div className="card" style={{marginBottom: '2rem'}}>
        <div className="card-title" style={{background: '#c62828', color: '#fff'}}>
          Buscar Préstamo
        </div>
        <div style={{padding: '1rem'}}>
          {/* Búsqueda por ID */}
          <div style={{marginBottom: '1.5rem'}}>
            <div style={{display: 'flex', gap: '1rem', alignItems: 'flex-end'}}>
              <div style={{flex: 1}}>
                <label style={{display: 'block', marginBottom: '0.5rem', fontWeight: 600}}>
                  Préstamo ID
                </label>
                <input
                  type="number"
                  value={searchId}
                  onChange={(e) => setSearchId(e.target.value)}
                  placeholder="Ingrese el ID del préstamo"
                  className="form-control"
                />
              </div>
              <button
                onClick={buscarPorId}
                className="btn"
                style={{background: '#c62828', color: '#fff', padding: '0.75rem 2rem'}}
                disabled={loading}
              >
                Buscar por ID
              </button>
            </div>
          </div>

          {/* Separador */}
          <div style={{
            borderTop: '2px dashed #ddd',
            margin: '1.5rem 0',
            position: 'relative'
          }}>
            <span style={{
              position: 'absolute',
              top: '-12px',
              left: '50%',
              transform: 'translateX(-50%)',
              background: '#fff',
              padding: '0 1rem',
              color: '#666',
              fontWeight: 600
            }}>
              O
            </span>
          </div>

          {/* Búsqueda por Cliente */}
          <div>
            <label style={{display: 'block', marginBottom: '0.5rem', fontWeight: 600}}>
              Buscar por Cliente
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                className="form-control"
                placeholder="Buscar por nombre, cédula, apodo o ID..."
                value={busquedaCliente}
                onChange={(e) => {
                  setBusquedaCliente(e.target.value);
                  setMostrarDropdown(true);
                }}
                onFocus={() => setMostrarDropdown(true)}
                disabled={loading}
              />
              {clienteObj && (
                <button
                  type="button"
                  onClick={limpiarCliente}
                  style={{
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'transparent',
                    border: 'none',
                    fontSize: '20px',
                    cursor: 'pointer',
                    color: '#6c757d'
                  }}
                >
                  ×
                </button>
              )}

              {mostrarDropdown && busquedaCliente && clientesFiltrados.length > 0 && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  backgroundColor: 'white',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  maxHeight: '400px',
                  overflowY: 'auto',
                  zIndex: 1000,
                  boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                }}>
                  {clientesFiltrados.map((cliente) => (
                    <div
                      key={cliente.id}
                      onClick={() => seleccionarCliente(cliente)}
                      style={{
                        padding: '10px',
                        cursor: 'pointer',
                        borderBottom: '1px solid #f0f0f0',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                    >
                      {cliente.foto_url ? (
                        <img
                          src={cliente.foto_url}
                          alt={cliente.nombres}
                          style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '50%',
                            objectFit: 'cover'
                          }}
                        />
                      ) : (
                        <div style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '50%',
                          backgroundColor: '#007bff',
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 'bold'
                        }}>
                          {cliente.nombres?.charAt(0)}{cliente.apellidos?.charAt(0)}
                        </div>
                      )}
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 'bold' }}>
                          {cliente.nombres} {cliente.apellidos}
                          {cliente.apodo && (
                            <span style={{
                              marginLeft: '8px',
                              color: '#007bff',
                              fontStyle: 'italic',
                              fontWeight: 'normal'
                            }}>
                              ({cliente.apodo})
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: '0.9em', color: '#666' }}>
                          Cédula: {cliente.cedula} • ID: {cliente.id}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Lista de préstamos del cliente */}
          {prestamosPorCliente.length > 0 && (
            <div style={{marginTop: '1rem'}}>
              <label style={{display: 'block', marginBottom: '0.5rem', fontWeight: 600}}>
                Préstamos del Cliente
              </label>
              <div style={{
                maxHeight: '200px',
                overflowY: 'auto',
                border: '1px solid #ddd',
                borderRadius: '4px'
              }}>
                {prestamosPorCliente.map(prestamo => (
                  <div
                    key={prestamo.id}
                    onClick={() => handleSeleccionarPrestamo(prestamo.id)}
                    style={{
                      padding: '0.75rem',
                      borderBottom: '1px solid #eee',
                      cursor: 'pointer',
                      background: prestamoSeleccionado?.id === prestamo.id ? '#ffebee' : '#fff',
                      transition: 'background 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      if (prestamoSeleccionado?.id !== prestamo.id) {
                        e.target.style.background = '#f5f5f5';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (prestamoSeleccionado?.id !== prestamo.id) {
                        e.target.style.background = '#fff';
                      }
                    }}
                  >
                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                      <div>
                        <strong>ID: {prestamo.id}</strong> - {prestamo.modalidad}
                      </div>
                      <div style={{textAlign: 'right'}}>
                        <div style={{fontWeight: 'bold', color: '#27ae60'}}>
                          ${prestamo.total.toLocaleString('es-DO', {minimumFractionDigits: 2})}
                        </div>
                        <span className={`badge badge-${
                          prestamo.estado === 'activo' ? 'success' :
                          prestamo.estado === 'pagado' ? 'info' : 'warning'
                        }`}>
                          {prestamo.estado}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {clienteSeleccionado && prestamosPorCliente.length === 0 && !loading && (
            <div style={{
              marginTop: '1rem',
              padding: '1rem',
              background: '#fff3cd',
              border: '1px solid #ffc107',
              borderRadius: '4px',
              color: '#856404'
            }}>
              Este cliente no tiene préstamos registrados
            </div>
          )}
        </div>
      </div>

      {/* Información del Préstamo */}
      {prestamoSeleccionado && (
        <div className="card" style={{marginBottom: '2rem', border: '2px solid #c62828'}}>
          <div className="card-title" style={{background: '#c62828', color: '#fff'}}>
            Información del Préstamo a Eliminar
          </div>

          <div style={{padding: '1rem', background: '#ffebee'}}>
            <div className="form-grid">
              <div className="form-group">
                <label>ID</label>
                <div style={{fontWeight: 'bold'}}>{prestamoSeleccionado.id}</div>
              </div>

              <div className="form-group">
                <label>Cliente</label>
                <div style={{fontWeight: 'bold'}}>
                  {prestamoSeleccionado.cliente?.nombres} {prestamoSeleccionado.cliente?.apellidos}
                </div>
              </div>

              <div className="form-group">
                <label>Capital</label>
                <div style={{fontWeight: 'bold'}}>{formatMoney(prestamoSeleccionado.capital)}</div>
              </div>

              <div className="form-group">
                <label>Total</label>
                <div style={{fontWeight: 'bold'}}>{formatMoney(prestamoSeleccionado.total)}</div>
              </div>

              <div className="form-group">
                <label>Tasa</label>
                <div>{prestamoSeleccionado.tasa}%</div>
              </div>

              <div className="form-group">
                <label>Modalidad</label>
                <div>{prestamoSeleccionado.modalidad}</div>
              </div>

              <div className="form-group">
                <label>Tipo</label>
                <div>{prestamoSeleccionado.tipo}</div>
              </div>

              <div className="form-group">
                <label>Estado</label>
                <div>
                  <span className={`badge ${prestamoSeleccionado.estado === 'activo' ? 'badge-success' : 'badge-warning'}`}>
                    {prestamoSeleccionado.estado}
                  </span>
                </div>
              </div>

              <div className="form-group">
                <label>Fecha de Inicio</label>
                <div>{formatDate(prestamoSeleccionado.fecha_inicio)}</div>
              </div>

              <div className="form-group">
                <label>Cuotas</label>
                <div>{prestamoSeleccionado.cuotas_pagadas || 0} de {prestamoSeleccionado.cuotas}</div>
              </div>
            </div>
          </div>

          {/* Confirmación */}
          <div style={{padding: '1.5rem', borderTop: '1px solid #e0e0e0', background: '#fff3e0'}}>
            <div className="form-group">
              <div style={{display: 'flex', alignItems: 'center', gap: '1rem'}}>
                <input
                  type="checkbox"
                  id="confirmar"
                  checked={confirmacion}
                  onChange={(e) => setConfirmacion(e.target.checked)}
                  style={{width: '20px', height: '20px', cursor: 'pointer'}}
                />
                <label
                  htmlFor="confirmar"
                  style={{margin: 0, cursor: 'pointer', fontSize: '1rem', fontWeight: 600, color: '#c62828'}}
                >
                  Confirmo que deseo eliminar este préstamo
                </label>
              </div>
            </div>

            <div className="banner" style={{marginTop: '1rem', background: '#fff3e0', border: '1px solid #ff9800', color: '#e65100'}}>
              ⚠️ <strong>Aviso:</strong> Los reenganches creados a partir de (fecha) se eliminarán a través de 'Eliminar Préstamo'; esta vista desaparecerá en un futuro.
            </div>
          </div>

          {/* Botones */}
          <div style={{padding: '1rem', display: 'flex', gap: '1rem', justifyContent: 'flex-end'}}>
            <button
              onClick={() => {
                setPrestamoSeleccionado(null);
                setSearchId('');
                setConfirmacion(false);
              }}
              className="btn"
              style={{background: '#95a5a6'}}
              disabled={loading}
            >
              Cancelar
            </button>
            <button
              onClick={handleEliminar}
              className="btn"
              style={{background: '#c62828', color: '#fff'}}
              disabled={loading || !confirmacion}
            >
              {loading ? 'Eliminando...' : 'Eliminar'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default EliminarPrestamo;
